import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Download, Calendar, Maximize2, MessageSquare, Mail, CheckCheck, MousePointerClick, AlertCircle, MessageCircle, Phone, ExternalLink, BarChart3, Award, Check, AlertTriangle, HelpCircle, Database, Send, Eye } from 'lucide-react'
import { Label, PolarRadiusAxis, RadialBar, RadialBarChart } from 'recharts'
import { ChartContainer, type ChartConfig } from '@/shared/components/ui/chart'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useAuth } from '@/auth/context/AuthContext'
import { canUpdateReports } from '@/shared/lib/utils'
import { useReporteWhatsAppPorCampania, useReportePorCampania, useRespuestasWhatsApp, useScoreWhatsApp } from '@/modules/cronograma/hooks/useReportes'
import { useCampania } from '@/modules/multichannel/hooks/useCampanias'
import { ConversionFunnel, type FunnelStep, SCORE_PALETTES, DEFAULT_PALETTE } from '@/modules/multichannel/components/ConversionFunnel'
import { RedirectBanner } from '@/modules/multichannel/components/RedirectBanner'
import { SuperMetricCard, GaugeChart, BenchmarkBarChart, catColorMap } from '@/modules/multichannel/components/ReportCharts'
import { useBreadcrumb } from '@/shared/context/BreadcrumbContext'


export function ReporteWhatsAppDetalle() {
  const { id } = useParams<{ id: string }>()
  const campaniaId = Number(id)
  const navigate = useNavigate()
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [showBenchmarkInfo, setShowBenchmarkInfo] = useState(false)

  const { user } = useAuth()
  const showActualizado = canUpdateReports(user)
  const { data: reporte, isLoading, error } = useReporteWhatsAppPorCampania(campaniaId, true)
  const { data: campania } = useCampania(campaniaId)
  const { data: reporteMailing } = useReportePorCampania(campaniaId, true)
  const { data: respuestas = [] } = useRespuestasWhatsApp(reporte?.id || null, !!reporte?.id)
  const { data: score } = useScoreWhatsApp(reporte?.id || null, !!reporte?.id)

  const { setOverride, clearOverride } = useBreadcrumb()

  useEffect(() => {
    if (reporte?.titulo_campana) {
      setOverride(`/multichannel/reporte-whatsapp/${id}`, `Reporte WA: ${reporte.titulo_campana}`)
    }
    return () => clearOverride(`/multichannel/reporte-whatsapp/${id}`)
  }, [reporte?.titulo_campana, id, setOverride, clearOverride])

  const formatNum = (n: number) => new Intl.NumberFormat('es-EC').format(n)
  const formatPct = (n: number) => `${n.toFixed(2)}%`

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-[400px]" />
            <Skeleton className="h-4 w-[300px]" />
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-[200px]" />
          <Skeleton className="h-[200px]" />
          <Skeleton className="h-[200px]" />
        </div>
      </div>
    )
  }

  if (error || !reporte) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <MessageSquare className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold mb-2">Reporte WhatsApp no encontrado</h2>
        <p className="text-muted-foreground mb-6">
          No se encontró un reporte de WhatsApp para esta campaña
        </p>
        <Button onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Button>
      </div>
    )
  }

  // Calcular tasas (consistente con backend: models.py ReporteCampaniaWhatsApp)
  const tasaEntrega = reporte.base > 0 ? (reporte.entrega / reporte.base) * 100 : 0
  const tasaLectura = reporte.base > 0 ? (reporte.leidos / reporte.base) * 100 : 0
  const tasaClics = reporte.entrega > 0 ? (reporte.clics / reporte.entrega) * 100 : 0
  const tasaError = reporte.base > 0 ? (reporte.unsub / reporte.base) * 100 : 0

  // Score
  const scoreCalificacion = score?.calificacion?.toUpperCase() || null
  const scoreColors = catColorMap[scoreCalificacion || 'C'] || catColorMap['C']

  // Benchmarks del backend (vienen del score endpoint)
  const benchmarkEntrega = score?.benchmark_entrega ?? undefined
  const benchmarkAperturas = score?.benchmark_aperturas ?? undefined
  const benchmarkClics = score?.benchmark_clics ?? undefined

  // Fortalezas y áreas de mejora (basadas en categorías del backend)
  const fortalezas: string[] = []
  const mejoras: string[] = []

  const catEntrega = reporte.categoria_entrega?.toUpperCase()
  const catAperturas = reporte.categoria_aperturas?.toUpperCase()
  const catClics = reporte.categoria_clics?.toUpperCase()

  if (catEntrega === 'A+' || catEntrega === 'A') fortalezas.push('Excelente entregabilidad')
  else if (catEntrega) mejoras.push('Revisar calidad de la base de datos')

  if (catAperturas === 'A+' || catAperturas === 'A') fortalezas.push('Alta tasa de lectura')
  else if (catAperturas) mejoras.push('Optimizar horario de envío')

  if (catClics === 'A+' || catClics === 'A') fortalezas.push('CTAs efectivos')
  else if (catClics) mejoras.push('Mejorar llamadas a la acción')

  // Funnel steps para WhatsApp (colores según score)
  const waPalette = SCORE_PALETTES[scoreCalificacion || ''] || DEFAULT_PALETTE
  const waFunnelSteps: FunnelStep[] = [
    { label: 'Base dirigida', value: reporte.base, percentage: 100, color: waPalette[0] },
    { label: 'Entregados', value: reporte.entrega, percentage: tasaEntrega, color: waPalette[1] },
    { label: 'Leídos', value: reporte.leidos, percentage: tasaLectura, color: waPalette[2] },
    { label: 'Clics', value: reporte.clics, percentage: tasaClics, color: waPalette[3] },
  ]

  const getTipoBadge = (tipo: string) => {
    const colores: Record<string, string> = {
      positiva: 'bg-green-100 text-green-800',
      negativa: 'bg-red-100 text-red-800',
      neutral: 'bg-gray-100 text-gray-800',
      consulta: 'bg-blue-100 text-blue-800',
      solicitud: 'bg-purple-100 text-purple-800',
      queja: 'bg-orange-100 text-orange-800',
      otro: 'bg-gray-100 text-gray-800',
    }
    return colores[tipo] || colores.otro
  }

  const getEstadoBadge = (estado: string) => {
    const colores: Record<string, string> = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      en_proceso: 'bg-blue-100 text-blue-800',
      respondida: 'bg-green-100 text-green-800',
      cerrada: 'bg-gray-100 text-gray-800',
    }
    return colores[estado] || colores.pendiente
  }

  return (
    <div className="space-y-6">
      {/* Page Heading */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-muted-foreground" />
            Reporte de Campaña
          </h1>
          {reporte.completado && reporte.dias_recopilacion && (
            <Badge variant="outline" className="text-green-600 border-green-300">
              <Check className="h-3 w-3 mr-1" />
              Completado ({reporte.dias_recopilacion} días)
            </Badge>
          )}
          {showActualizado && (
            reporte.actualizado ? (
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 dark:text-emerald-400">
                <Check className="h-3 w-3 mr-1" />
                Actualizado
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Pendiente de actualizar
              </Badge>
            )
          )}
        </div>
        <div className="flex items-center gap-2">
          {reporteMailing && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/multichannel/reporte/${campaniaId}`}>
                <Mail className="mr-2 h-4 w-4" />
                Reporte Email
              </Link>
            </Button>
          )}
          {reporte.habilitar_boton_excel && reporte.url_excel && (
            <a
              href={reporte.url_excel}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Excel
            </a>
          )}
        </div>
      </div>

      {/* Header - Row 1: Título (col-8) + Cronograma (col-4) */}
      <div className="grid gap-4 md:grid-cols-12">
        {/* Título + Score */}
        <Card
          className="md:col-span-8 relative overflow-visible border-gray-200 dark:border-gray-700"
          style={{ background: `linear-gradient(to right, ${scoreColors.hex}20, transparent 60%)` }}
        >
          <CardContent className="h-full flex items-center py-4">
            <div className="flex items-center gap-3 flex-1 pr-24">
              <Calendar className="h-8 w-8 text-muted-foreground shrink-0" />
              <div>
                <h2 className="text-2xl font-bold">{reporte.titulo_campana}</h2>
                <p className="text-sm text-muted-foreground">
                  Campaña WhatsApp{campania?.marca_principal_nombre ? ` • ${campania.marca_principal_nombre}` : ''}
                </p>
              </div>
            </div>
            {score && (
              <div className="flex flex-col items-center shrink-0">
                <Badge className={`${scoreColors.bg} ${scoreColors.text} text-[10px] mb-1 gap-1`}>
                  {scoreCalificacion && scoreColors.icon}
                  {scoreCalificacion ? scoreColors.label : 'Sin datos'}
                </Badge>
                <div className="bg-background rounded-full p-0.5 shadow">
                  <div
                    className={`rounded-full flex items-center justify-center ${scoreColors.bg} ${scoreColors.text}`}
                    style={{ width: '44px', height: '44px' }}
                  >
                    <span className="text-sm font-bold">{score.calificacion}</span>
                  </div>
                </div>
                <span className="text-[10px] font-bold mt-0.5">{score.score_final}/100</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cronograma */}
        <Card className="md:col-span-4">
          <CardContent className="pt-6 flex items-center h-full">
            <div className="flex-1 space-y-2">
              {reporte.cronogramas && reporte.cronogramas.length > 0 ? (
                reporte.cronogramas.map((cronograma) => (
                  <div key={cronograma.id}>
                    <Badge variant="secondary" className="text-xs font-normal">
                      {cronograma.descripcion || `Envío #${cronograma.orden}`} •{' '}
                      {new Intl.DateTimeFormat('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(cronograma.fecha_hora))}
                      {` • ${new Intl.DateTimeFormat('es-EC', { hour: '2-digit', minute: '2-digit' }).format(new Date(cronograma.fecha_hora))}`}
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">Sin información de envío</p>
              )}
              {reporte.completado && reporte.dias_recopilacion && (
                <Badge variant="outline" className="text-[10px] text-green-600 border-green-300">
                  <Check className="h-3 w-3 mr-1" />
                  Completado ({reporte.dias_recopilacion} días)
                </Badge>
              )}
            </div>
            <div className="ml-3 rounded-full bg-muted p-3">
              <Calendar className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Banner de redirección */}
      {reporte.es_redireccion && reporte.reporte_relacionado_campania_nombre && (
        <RedirectBanner campaniaOrigenNombre={reporte.reporte_relacionado_campania_nombre} />
      )}

      {/* Métricas principales */}
      <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-5">
        <div
          className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
          style={{ background: 'linear-gradient(to right, #25D36620, transparent 60%)' }}
        >
          <div className="px-4 py-3">
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-2">Base Total</p>
            <div className="flex items-center gap-3">
              <Database className="h-10 w-10 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0 text-right">
                <div className="text-2xl font-bold text-foreground leading-tight">
                  {formatNum(reporte.base)}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {formatNum(reporte.entrega)} entregados
                </p>
                <p className="text-[10px] text-muted-foreground">
                  Conversión: {formatPct(reporte.base > 0 ? (reporte.clics / reporte.base) * 100 : 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
        <SuperMetricCard
          icon={<CheckCheck className="h-10 w-10" />}
          label="Entregados"
          percentage={reporte.tasa_entrega || tasaEntrega}
          count={reporte.entrega}
          benchmark={benchmarkEntrega}
          category={reporte.categoria_entrega ?? null}
        />
        <SuperMetricCard
          icon={<Eye className="h-10 w-10" />}
          label="Leídos"
          percentage={reporte.tasa_aperturas || tasaLectura}
          count={reporte.leidos}
          benchmark={benchmarkAperturas}
          category={reporte.categoria_aperturas ?? null}
        />
        <SuperMetricCard
          icon={<MousePointerClick className="h-10 w-10" />}
          label="Clics"
          percentage={reporte.tasa_clics || tasaClics}
          count={reporte.clics}
          benchmark={benchmarkClics}
          category={reporte.categoria_clics ?? null}
        />
        <SuperMetricCard
          icon={<AlertCircle className="h-10 w-10" />}
          label="Errores / Unsub"
          percentage={tasaError}
          count={reporte.unsub}
        />
      </div>

      {/* Embudo de Conversión + Imagen del mensaje */}
      <div className="grid gap-6 md:grid-cols-2">
        <ConversionFunnel
          base={reporte.base}
          entrega={reporte.entrega}
          aperturas={reporte.leidos}
          clics={reporte.clics}
          tasa_entrega={tasaEntrega}
          tasa_aperturas={tasaLectura}
          tasa_clics={tasaClics}
          customSteps={waFunnelSteps}
        />

        {/* Simulación Chat WhatsApp */}
        <Card className="overflow-hidden flex flex-col">
          <CardHeader className="py-3 bg-muted/30">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-bold">Vista Previa del Mensaje</CardTitle>
              {reporte.imagen_url && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsPreviewOpen(true)}
                >
                  <Maximize2 className="mr-2 h-3 w-3" />
                  Expandir
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1">
            <div
              className="p-4 h-full flex items-center justify-center"
              style={{ backgroundColor: '#efeae2' }}
            >
              {/* Burbuja + emoji */}
              <div className="flex items-center gap-1.5">
                <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm" style={{ maxWidth: '280px' }}>
                  {/* Imagen */}
                  {reporte.imagen_url && (
                    <div
                      className="cursor-pointer hover:opacity-90 transition-opacity px-1.5 pt-1.5"
                      onClick={() => setIsPreviewOpen(true)}
                    >
                      <img
                        src={reporte.imagen_url}
                        alt="Imagen del mensaje"
                        className="w-full h-auto max-h-[260px] object-cover rounded-lg"
                      />
                    </div>
                  )}

                  {/* Texto */}
                  {reporte.mensaje_texto && (
                    <div className="px-3 py-2">
                      <p
                        className="text-[13px] whitespace-pre-wrap text-gray-900 dark:text-gray-100 leading-snug"
                        dangerouslySetInnerHTML={{
                          __html: reporte.mensaje_texto
                            .replace(/\{\{nombre\}\}/gi, user?.first_name || 'Usuario')
                            .replace(/\{nombre\}/gi, user?.first_name || 'Usuario')
                            .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
                        }}
                      />
                      <div className="flex items-center justify-end gap-1 mt-1">
                        <span className="text-[10px] text-gray-500">10:30 a.m.</span>
                        <CheckCheck className="h-3.5 w-3.5 text-blue-500" />
                      </div>
                    </div>
                  )}

                  {/* Botones CTA */}
                  {reporte.botones_cta && reporte.botones_cta.length > 0 && (
                    <div className="border-t border-gray-200 dark:border-gray-700">
                      {reporte.botones_cta.map((boton, index) => (
                        <a
                          key={index}
                          href={boton.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center justify-center gap-2 px-3 py-2.5 text-[13px] font-medium text-[#00a884] hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                            index < reporte.botones_cta!.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''
                          }`}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          {boton.texto}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                {/* Emoji icon */}
                <div className="w-7 h-7 rounded-full bg-white dark:bg-gray-700 shadow-sm flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Respuestas + Gauge Charts */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Respuestas Recibidas (en lugar de Base Dirigida) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-muted-foreground" />
              Respuestas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {respuestas.length > 0 ? (
              <div className="space-y-2">
                <div className="text-2xl font-bold">{respuestas.length}</div>
                <div className="space-y-1">
                  {(() => {
                    const tipos = respuestas.reduce<Record<string, number>>((acc, r) => {
                      acc[r.tipo] = (acc[r.tipo] || 0) + 1
                      return acc
                    }, {})
                    return Object.entries(tipos).map(([tipo, count]) => (
                      <div key={tipo} className="flex items-center justify-between text-xs">
                        <span className={`px-1.5 py-0.5 rounded-full ${getTipoBadge(tipo)}`}>{tipo}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))
                  })()}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Sin respuestas</p>
            )}
          </CardContent>
        </Card>

        <GaugeChart
          value={reporte.tasa_entrega || tasaEntrega}
          benchmark={benchmarkEntrega ?? 0}
          label="Tasa de Entrega"
          category={reporte.categoria_entrega ?? null}
          icon={<Send className="h-3.5 w-3.5 text-muted-foreground" />}
        />
        <GaugeChart
          value={reporte.tasa_aperturas || tasaLectura}
          benchmark={benchmarkAperturas ?? 0}
          label="Tasa de Lectura"
          category={reporte.categoria_aperturas ?? null}
          icon={<Eye className="h-3.5 w-3.5 text-muted-foreground" />}
        />
        <GaugeChart
          value={reporte.tasa_clics || tasaClics}
          benchmark={benchmarkClics ?? 0}
          label="Tasa de Clics"
          category={reporte.categoria_clics ?? null}
          icon={<MousePointerClick className="h-3.5 w-3.5 text-muted-foreground" />}
        />
      </div>

      {/* Detalle de respuestas */}
      {respuestas.length > 0 && (
        <Card>
          <CardHeader className="py-3 bg-muted/30">
            <CardTitle className="text-base font-bold flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-green-600" />
              Detalle de Respuestas
              <Badge variant="secondary" className="text-xs">{respuestas.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {respuestas.map((respuesta) => (
                <div
                  key={respuesta.id}
                  className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-4 border border-gray-200 dark:border-gray-700 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {respuesta.telefono && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Phone className="h-3 w-3" />
                          {respuesta.telefono}
                        </span>
                      )}
                      {respuesta.nombre && (
                        <span className="text-xs font-medium">{respuesta.nombre}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getTipoBadge(respuesta.tipo)}`}>
                        {respuesta.tipo}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getEstadoBadge(respuesta.estado)}`}>
                        {respuesta.estado}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{respuesta.mensaje}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Análisis de Benchmark + Score General */}
      <div className="grid gap-6 md:grid-cols-12">
        <Card className="md:col-span-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <BarChart3 className="h-5 w-5 shrink-0" />
              <span>Análisis Comparativo de Benchmark</span>
              <button
                onClick={() => setShowBenchmarkInfo(!showBenchmarkInfo)}
                className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
              >
                <HelpCircle className="h-4 w-4" />
              </button>
            </CardTitle>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                showBenchmarkInfo ? 'max-h-8 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="text-sm text-muted-foreground">
                Rendimiento de la campaña vs estándares de la industria WhatsApp
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-6">
              <BenchmarkBarChart
                tasa={reporte.tasa_entrega || tasaEntrega}
                benchmark={benchmarkEntrega ?? 0}
                label="Tasa de Entrega"
                category={reporte.categoria_entrega ?? null}
                icon={<Send className="h-4 w-4 text-muted-foreground" />}
              />
              <BenchmarkBarChart
                tasa={reporte.tasa_aperturas || tasaLectura}
                benchmark={benchmarkAperturas ?? 0}
                label="Tasa de Lectura"
                category={reporte.categoria_aperturas ?? null}
                icon={<Eye className="h-4 w-4 text-muted-foreground" />}
              />
              <BenchmarkBarChart
                tasa={reporte.tasa_clics || tasaClics}
                benchmark={benchmarkClics ?? 0}
                label="Tasa de Clics"
                category={reporte.categoria_clics ?? null}
                icon={<MousePointerClick className="h-4 w-4 text-muted-foreground" />}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-4">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                Score General
              </CardTitle>
              <Badge className={`${scoreColors.bg} ${scoreColors.text} gap-1`}>
                {scoreCalificacion && scoreColors.icon}
                {scoreCalificacion ? scoreColors.label : 'Sin datos'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ChartContainer
              config={{
                score: { label: 'Score', color: scoreColors.hex },
              } satisfies ChartConfig}
              className="mx-auto aspect-square max-h-[160px]"
            >
              <RadialBarChart
                data={[{ score: score?.score_final ?? 0, fill: scoreColors.hex }]}
                startAngle={90}
                endAngle={90 - ((score?.score_final ?? 0) / 100) * 360}
                outerRadius={70}
                innerRadius={58}
              >
                <RadialBar dataKey="score" background cornerRadius={10} />
                <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                  <Label
                    content={({ viewBox }) => {
                      if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                        return (
                          <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                            <tspan x={viewBox.cx} y={(viewBox.cy || 0) - 8} className="fill-foreground text-2xl font-bold">
                              {score?.calificacion || '-'}
                            </tspan>
                            <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 14} className="fill-muted-foreground text-xs">
                              {score?.score_final ?? '-'}/100
                            </tspan>
                          </text>
                        )
                      }
                    }}
                  />
                </PolarRadiusAxis>
              </RadialBarChart>
            </ChartContainer>

            {fortalezas.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-green-600" />
                  Fortalezas
                </h4>
                <ul className="space-y-1">
                  {fortalezas.map((f) => (
                    <li key={f} className="text-xs text-muted-foreground flex items-start gap-1.5">
                      <Check className="h-3 w-3 text-green-600 mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {mejoras.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-1.5 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Áreas de Mejora
                </h4>
                <ul className="space-y-1">
                  {mejoras.map((m) => (
                    <li key={m} className="text-xs text-muted-foreground flex items-start gap-1.5">
                      <AlertTriangle className="h-3 w-3 text-amber-500 mt-0.5 shrink-0" />
                      {m}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {reporte.conclusion && (
              <div className="border-t pt-3">
                <h4 className="text-sm font-medium mb-1.5">Conclusión</h4>
                <p className="text-xs text-muted-foreground whitespace-pre-wrap">
                  {reporte.conclusion}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal de Vista Previa de Imagen */}
      {reporte.imagen_url && (
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className="max-w-fit max-h-[90vh] p-0">
            <DialogHeader className="p-6 pb-4">
              <DialogTitle>Imagen del Mensaje WhatsApp</DialogTitle>
              <DialogDescription>
                Vista ampliada de la imagen enviada
              </DialogDescription>
            </DialogHeader>
            <div className="px-6 pb-6">
              <div className="rounded-lg overflow-hidden">
                <img
                  src={reporte.imagen_url}
                  alt="Imagen del mensaje WhatsApp"
                  className="h-auto max-h-[70vh] object-contain"
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
